import { SELF_REPORTED_CREDIT_LOOKUP } from '../../constants';
import { calculateAffforability, calculateHomePriceBasedOnDownPayment } from './calculations';
import { getConfigs } from './configs';

const PROGRAM = 'Fixed30Year';
const LOAN_TYPE = 'Conventional';
const BE_CREDIT_SCORE = {
    Low: 'Low',
    High: 'High',
    VeryHigh: 'VeryHigh',
};
const CREDIT_SCORE_MAPPING = {
    [SELF_REPORTED_CREDIT_LOOKUP.EXCELLENT]: BE_CREDIT_SCORE.VeryHigh,
    [SELF_REPORTED_CREDIT_LOOKUP.GOOD]: BE_CREDIT_SCORE.High,
    [SELF_REPORTED_CREDIT_LOOKUP.AVERAGE]: BE_CREDIT_SCORE.Low,
    [SELF_REPORTED_CREDIT_LOOKUP.POOR]: BE_CREDIT_SCORE.Low,
    [SELF_REPORTED_CREDIT_LOOKUP.BAD]: BE_CREDIT_SCORE.Low,
};

const doFetch = async (...args) => {
    const response = await fetch(...args);
    const data = await response.json();
    if (response.ok) {
        return data;
    }
    return Promise.reject(data);
};

export const buildUrlQueryParams = query => {
    const filteredQuery = Object.entries(query).reduce(
        (acc, [key, val]) => (val === undefined || val === null || val === '' ? acc : { ...acc, [key]: val }),
        {}
    );
    return new URLSearchParams(filteredQuery).toString();
};

const buildDotNotationQueryParams = query => {
    const arr = [];
    const recurse = (v, k) => {
        if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
            // Primitives
            arr.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
        } else if (typeof v === 'object' && v !== null && (Object.keys(v).length > 0 || v.length)) {
            // Non-empty objects/arrays
            Object.keys(v).forEach(kk => {
                recurse(v[kk], k + '.' + kk);
            });
        } else if (typeof v !== 'undefined') {
            // Empty objects/arrays, null
            arr.push(`${encodeURIComponent(k)}=`);
        }
    };

    Object.keys(query).forEach(k => {
        recurse(query[k], k);
    });

    return arr.length ? `${arr.join('&')}` : '';
};
export const queryAutocomplete = async inputs => {
    const { autocompleteBaseUrl } = getConfigs();

    const query = buildUrlQueryParams(inputs);
    const url = `${autocompleteBaseUrl}/v3/suggestions?${query}`;
    try {
        return await doFetch(url);
    } catch (error) {
        // TODO logging
    }
};

export const getRegionInformation = async (latitude, longitude) => {
    const { maxBaseUrl, partnerId } = getConfigs();
    const query = buildDotNotationQueryParams({
        locationRef: { coordinates: { latitude, longitude } },
        partnerId,
    });

    const url = `${maxBaseUrl}/getRegionInformation?${query}`;
    try {
        const response = await doFetch(url);
        return response;
    } catch (error) {
        // TODO logging
    }
};

// TODO consume WIP getCurrentZHLRates v2
const cachedRate = {};
const _fetchRate = async (maxBaseUrl, query) => {
    if (cachedRate[query] >= 0) {
        return cachedRate[query];
    }

    const response = await doFetch(`${maxBaseUrl}/getCurrentRates?${query}`);
    cachedRate[query] = response?.rates?.[PROGRAM]?.rate;
    return cachedRate[query];
};

const _buildRateQuery = ({ partnerId, creditScore, propertyBucket }) =>
    buildDotNotationQueryParams({
        partnerId,
        queries: {
            [PROGRAM]: {
                program: PROGRAM,
                loanType: LOAN_TYPE,
                creditScoreBucket: CREDIT_SCORE_MAPPING[creditScore],
                propertyBucket,
            },
        },
    });

const buildDefaultRateQuery = ({ partnerId, creditScore }) => _buildRateQuery({ partnerId, creditScore });
const buildRateQueryWithPropertyBucket = ({ partnerId, homePrice, downPayment, zipCode, creditScore }) => {
    const propertyValue = Math.round(homePrice);
    // fallback to national rate if zip code is not available
    const location = zipCode ? { zipCode } : { stateAbbreviation: 'US' };
    const propertyBucket = {
        propertyValue,
        loanAmount: Math.round(propertyValue - downPayment),
        location,
    };

    return _buildRateQuery({ partnerId, creditScore, propertyBucket });
};

const fetchRate = async ({ homePrice, downPayment, zipCode, creditScore }) => {
    const { maxBaseUrl, partnerId } = getConfigs();

    // location specific rate
    const zipCodeRateQuery = buildRateQueryWithPropertyBucket({
        partnerId,
        homePrice,
        downPayment,
        zipCode,
        creditScore,
    });
    const zipCodeRate = await _fetchRate(maxBaseUrl, zipCodeRateQuery);
    if (zipCodeRate > 0) {
        return zipCodeRate;
    }

    // fallback to national rate
    const nationalRateQuery = buildRateQueryWithPropertyBucket({ partnerId, homePrice, downPayment, creditScore });
    const nationalRate = await _fetchRate(maxBaseUrl, nationalRateQuery);
    if (nationalRate > 0) {
        return nationalRate;
    }

    // default fallback due to jumbo and low credit score combination
    const defaultRateQuery = buildDefaultRateQuery({ partnerId, creditScore });
    return await _fetchRate(maxBaseUrl, defaultRateQuery);
};

const cachedAnnualPropertyTaxRates = {};
const getAnnualPropertyTaxRate = async zipCode => {
    if (cachedAnnualPropertyTaxRates[zipCode]) {
        return cachedAnnualPropertyTaxRates[zipCode];
    }

    const { maxBaseUrl, partnerId } = getConfigs();
    const query = buildUrlQueryParams({
        zipCode,
        partnerId,
    });
    const response = await doFetch(`${maxBaseUrl}/getAnnualPropertyTaxRate?${query}`);
    cachedAnnualPropertyTaxRates[zipCode] = response?.rate;
    return cachedAnnualPropertyTaxRates[zipCode];
};

export const performCalculation = async ({ annualIncome, monthlyDebts, downPayment, creditScore, zipCode, state }) => {
    const homePrice = calculateHomePriceBasedOnDownPayment(downPayment);
    const mortgageRate = await fetchRate({ homePrice, downPayment, creditScore, zipCode });
    const propertyTaxRate = await getAnnualPropertyTaxRate(zipCode);
    return calculateAffforability({
        annualIncome,
        monthlyDebts,
        downPayment,
        mortgageRate,
        propertyTaxRate,
        creditScore,
        state,
    });
};
